// Picks up a new release without anyone pressing Ctrl+F5. The hub is a single-page app, so a tab left open keeps
// running the code it loaded with until the page is loaded again. Every API reply carries the server's release
// (X-Briary-Version); once it differs from the one this tab started with:
//  - the next link anyone follows loads the page in full (the router asks updateWaiting()), so they simply land on
//    the new version;
//  - someone staying on one page sees a slim "updated" bar with a Reload button (never an automatic reload, which
//    could lose what they've typed).
// A quiet check every 5 minutes, and when the tab comes back into view, catches tabs that make no other requests.
import { useEffect, useSyncExternalStore } from 'react'

let loaded: string | null = null
let waiting = false
const listeners = new Set<() => void>()

/** Called with each reply's X-Briary-Version. The first one seen is the release this tab is running. */
export function noteVersion(version: string | null) {
  if (!version) return
  if (loaded == null) {
    loaded = version
    return
  }
  if (version !== loaded && !waiting) {
    waiting = true
    for (const l of listeners) l()
  }
}

/** True once the server is on a newer release than this tab. */
export const updateWaiting = () => waiting

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

async function check() {
  if (document.visibilityState !== 'visible') return
  try {
    const res = await fetch('/api/health', { cache: 'no-store', credentials: 'same-origin' })
    noteVersion(res.headers.get('x-briary-version'))
  } catch {
    // offline: try again next time
  }
}

export function UpdateBar() {
  const show = useSyncExternalStore(subscribe, updateWaiting, updateWaiting)
  useEffect(() => {
    void check()
    const timer = window.setInterval(() => void check(), 5 * 60_000)
    const onVisible = () => void check()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
  if (!show) return null
  return (
    <div className="update-bar" role="status">
      <span>Briary Hub has been updated. Reload to get the latest version (anything you haven't saved on this page will be lost).</span>
      <button type="button" className="btn btn-sm btn-primary" onClick={() => window.location.reload()}>Reload</button>
    </div>
  )
}
